import { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CloseIcon,
  Dialog,
  DialogContent,
  HourglassEmptyIcon,
  PlayCircleOutlineIcon,
  Skeleton,
  Text,
} from '@billda/design-system';
import { PermissionGate } from '../../../../../shared/components/gates/PermissionGate';
import { Permissions } from '@billda/permissions-ui';
import { useVariationImages } from '../../../hooks/useVariationImages';
import type { VariationImageResponse } from '../../../types/variationImage';
import {
  getCachedUrl,
  cacheImage,
} from '../../../services/variationImageCache';

interface VariationImageGalleryProps {
  variationRequestId: string;
  minimal?: boolean;
}

export function VariationImageGallery({
  variationRequestId,
  minimal,
}: VariationImageGalleryProps) {
  return (
    <PermissionGate permission={Permissions.Labour.VariationImages.Read}>
      <VariationImageGalleryContent
        variationRequestId={variationRequestId}
        minimal={minimal}
      />
    </PermissionGate>
  );
}

interface PreviewMedia {
  url: string;
  contentType: string;
}

function isVideo(contentType: string) {
  return contentType.startsWith('video/');
}

function VariationImageGalleryContent({
  variationRequestId,
  minimal,
}: VariationImageGalleryProps) {
  const {
    data: images,
    isLoading,
    error,
  } = useVariationImages(variationRequestId);
  const [preview, setPreview] = useState<PreviewMedia | null>(null);
  const [resolvedUrls, setResolvedUrls] = useState<Map<string, string>>(
    new Map()
  );

  const allImages = images ?? [];
  const uploadedImages = allImages.filter(img => img.status === 'Uploaded');
  const pendingCount = allImages.filter(img => img.status === 'Pending').length;

  // Resolve and cache blob URLs for uploaded images
  useEffect(() => {
    if (uploadedImages.length === 0) return;

    let cancelled = false;

    const resolve = async () => {
      const updates = new Map<string, string>();

      for (const img of uploadedImages) {
        if (!img.url) continue;
        const cached = getCachedUrl(img.contentHash);
        if (cached) {
          updates.set(img.id, cached);
        } else {
          try {
            const url = await cacheImage(img.contentHash, img.url);
            if (cancelled) return;
            updates.set(img.id, url);
          } catch {
            // Fall back to SAS URL if caching fails
            updates.set(img.id, img.url);
          }
        }
      }

      if (!cancelled) {
        setResolvedUrls(prev => {
          const next = new Map(prev);
          for (const [k, v] of updates) next.set(k, v);
          return next;
        });
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
    // Re-run when the image list changes (new uploads or refetch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedImages.map(img => img.contentHash).join(',')]);

  if (isLoading) {
    const grid = (
      <div
        className="grid grid-cols-2 gap-3"
        data-testid="variation-images-loading"
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            className="aspect-square rounded-lg"
          />
        ))}
      </div>
    );
    if (minimal) return grid;
    return (
      <Card>
        <CardContent className="pt-4">
          <Text variant="h6" className="mb-4">
            Photos
          </Text>
          {grid}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" data-testid="variation-images-error">
        Failed to load variation images
      </Alert>
    );
  }

  if (allImages.length === 0) {
    return minimal ? (
      <Text variant="body2" color="secondary">
        No photos added
      </Text>
    ) : null;
  }

  const handlePreview = (img: VariationImageResponse) => {
    const url = resolvedUrls.get(img.id) ?? img.url;
    if (url) setPreview({ url, contentType: img.contentType });
  };

  const grid = (
    <div
      data-testid="variation-images-gallery"
      className="grid grid-cols-2 gap-3"
    >
      {uploadedImages.map(img => {
        const displayUrl = resolvedUrls.get(img.id) ?? img.url;
        const video = isVideo(img.contentType);

        return (
          <button
            key={img.id}
            type="button"
            aria-label={`Preview ${img.originalFileName}`}
            data-testid={`variation-image-thumbnail-${img.id}`}
            onClick={() => handlePreview(img)}
            disabled={!displayUrl}
            className="group relative overflow-hidden rounded-lg border-0 bg-transparent p-0 disabled:cursor-default enabled:cursor-pointer"
          >
            {!displayUrl ? (
              <Skeleton
                variant="rectangular"
                className="aspect-square w-full"
              />
            ) : video ? (
              <>
                <video
                  src={displayUrl}
                  muted
                  className="block aspect-square w-full object-cover"
                />
                <PlayCircleOutlineIcon
                  className="pointer-events-none absolute top-1/2 left-1/2 size-9 -translate-x-1/2 -translate-y-1/2 text-on-inverse drop-shadow-md"
                  aria-hidden="true"
                />
              </>
            ) : (
              <img
                src={displayUrl}
                alt={img.originalFileName}
                className="block aspect-square w-full object-cover"
              />
            )}
          </button>
        );
      })}

      {/* Pending image placeholders */}
      {Array.from({ length: pendingCount }).map((_, i) => (
        <div
          key={`pending-${i}`}
          data-testid="variation-image-pending"
          className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg bg-surface-raised"
        >
          <HourglassEmptyIcon
            className="size-5 text-content-disabled"
            aria-hidden="true"
          />
          <Text
            variant="caption"
            color="disabled"
            className="px-1 text-center leading-tight"
          >
            Awaiting upload
          </Text>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {minimal ? (
        grid
      ) : (
        <Card>
          <CardContent className="pt-4">
            <div className="mb-4 flex items-center justify-between">
              <Text variant="h6">Photos</Text>
              <Text variant="body2" color="secondary">
                {allImages.length}
              </Text>
            </div>
            {grid}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!preview} onOpenChange={open => !open && setPreview(null)}>
        <DialogContent className="max-w-[90vw] bg-surface-inverse p-2">
          {preview && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPreview(null)}
                aria-label="Close preview"
                data-testid="variation-image-preview-close"
                className="absolute top-2 right-2 z-10 h-8 w-8 bg-surface-overlay p-0 text-on-inverse hover:bg-surface-overlay"
              >
                <CloseIcon className="size-4" aria-hidden="true" />
              </Button>
              {isVideo(preview.contentType) ? (
                <video
                  src={preview.url}
                  controls
                  autoPlay
                  className="block max-h-[85vh] max-w-full"
                />
              ) : (
                <img
                  src={preview.url}
                  alt="Preview"
                  className="block max-h-[85vh] max-w-full object-contain"
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
